/**
 * Homepage waitlist popup — builds pre-launch demand.
 * Triggers: ~4s dwell OR exit-intent, once per visitor (localStorage).
 * Copy intentionally does NOT advertise the setup-fee waiver (that's only for
 * invited email prospects, honoured privately on /registerwaitlist).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, ArrowRight, Clock, MailCheck } from 'lucide-react';

const SEEN_KEY = 'corvana_wl_popup_seen';

export default function WaitlistPopup() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const trigger = useCallback(() => {
    if (localStorage.getItem(SEEN_KEY)) return;
    localStorage.setItem(SEEN_KEY, '1');
    setOpen(true);
  }, []);

  useEffect(() => {
    if (localStorage.getItem(SEEN_KEY)) return undefined;
    const dwell = setTimeout(trigger, 4000);
    const onExit = (e) => { if (e.clientY <= 0) trigger(); };
    document.addEventListener('mouseout', onExit);
    return () => { clearTimeout(dwell); document.removeEventListener('mouseout', onExit); };
  }, [trigger]);

  const go = () => { setOpen(false); navigate('/get-started'); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setOpen(false)} data-testid="waitlist-popup">
          <motion.div initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="relative w-full max-w-md rounded-3xl bg-[#0b0b0f] text-white p-6 sm:p-7 shadow-2xl border border-white/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}>
            <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-indigo-600/30 blur-3xl" />
            <button onClick={() => setOpen(false)} data-testid="waitlist-popup-close"
              aria-label="Close waitlist popup"
              className="absolute top-3 right-3 inline-flex items-center justify-center h-9 w-9 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"><X className="w-5 h-5" /></button>

            <span className="relative inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-medium mb-4">
              <Sparkles className="w-3.5 h-3.5 text-indigo-300" /> Get started free · 14-day trial
            </span>
            <h3 className="relative text-2xl font-semibold tracking-tight leading-snug">
              Get started free with Corvana
            </h3>
            <p className="relative text-sm text-white/70 mt-3">
              Try Corvana on your own business with a 14-day free trial — and we'll
              hand-set-up your account on your real data at no setup cost.
            </p>

            <div className="relative mt-4 space-y-2 text-sm text-white/75">
              <p className="flex items-center gap-2"><MailCheck className="w-4 h-4 text-emerald-400 shrink-0" /> Free setup — we configure everything on your real data (normally 3 months' subscription).</p>
              <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-indigo-300 shrink-0" /> 14-day free trial — explore the full platform, cancel anytime.</p>
            </div>

            <button onClick={go} data-testid="waitlist-popup-cta"
              className="relative mt-6 w-full h-12 rounded-xl bg-white text-slate-900 font-medium flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors">
              Get started free <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} data-testid="waitlist-popup-dismiss"
              className="relative mt-2 w-full h-9 text-sm text-white/50 hover:text-white/80 transition-colors">Maybe later</button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
